import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import TodayIcon from "@mui/icons-material/Today";
import DateRangeIcon from "@mui/icons-material/DateRange";
import TuneIcon from "@mui/icons-material/Tune";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LinkIcon from "@mui/icons-material/Link";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HelpTooltip from "./HelpTooltip";

const PrayerWizardSetup = ({
  prayerPlan = "floating",
  onPrayerPlanChange,
  prayerFrequency = "Daily",
  onPrayerFrequencyChange,
  daysOfWeek = [],
  onDaysOfWeekChange,
  fromDate = "",
  onFromDateChange,
  toDate = "",
  onToDateChange,
  numberOfDays = "21",
  onNumberOfDaysChange,
  slotDuration = "30",
  onSlotDurationChange,
  fromTime = "00:00",
  onFromTimeChange,
  toTime = "23:59",
  onToTimeChange,
  chainDurationHours = "24",
  onChainDurationHoursChange,
  onComplete,
  onSkip,
  isLight = false,
}) => {
  const isChain = prayerPlan === "chain";
  const totalSteps = isChain ? 2 : 3;
  const [currentStep, setCurrentStep] = React.useState(1);
  const [isChainCustomDays, setIsChainCustomDays] = React.useState(false);
  const [chainCustomDaysVal, setChainCustomDaysVal] = React.useState("");
  const [isFloatingCustomDays, setIsFloatingCustomDays] = React.useState(false);
  const [isFixedCustomDays, setIsFixedCustomDays] = React.useState(false);
  const [fixedCustomDaysVal, setFixedCustomDaysVal] = React.useState("");

  // Keep step within valid bounds if switching between chain (2 steps) and regular (3 steps)
  useEffect(() => {
    if (isChain && currentStep > 2) {
      setCurrentStep(2);
    }
  }, [isChain, currentStep]);

  const formatDateStr = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  useEffect(() => {
    if (!fromTime && onFromTimeChange) onFromTimeChange("00:00");
    if (!toTime && onToTimeChange) onToTimeChange("23:59");
    if (!slotDuration && onSlotDurationChange) onSlotDurationChange("30");

    if (prayerPlan === "floating" && !numberOfDays && onNumberOfDaysChange) {
      onNumberOfDaysChange("21");
    }

    if ((prayerPlan === "fixed" || prayerPlan === "open") && !fromDate && onFromDateChange) {
      const start = new Date();
      const end = new Date();
      end.setDate(end.getDate() + 20);
      onFromDateChange(formatDateStr(start));
      if (onToDateChange) onToDateChange(formatDateStr(end));
    }

    if (prayerPlan === "chain") {
      if (!chainDurationHours && onChainDurationHoursChange) {
        onChainDurationHoursChange("24");
      }
      if (!fromDate && onFromDateChange) {
        onFromDateChange(formatDateStr(new Date()));
      }
      if (!fromTime && onFromTimeChange) {
        onFromTimeChange("00:00");
      }
      if (onPrayerFrequencyChange) {
        onPrayerFrequencyChange("Daily");
      }
    }
  }, [prayerPlan]);

  const chainHoursNum = parseFloat(chainDurationHours) || 24;
  const chainSlotMinsNum = parseInt(slotDuration, 10) || 30;
  const chainTotalMins = Math.round(chainHoursNum * 60);
  const isChainDivisible = chainSlotMinsNum > 0 && chainTotalMins % chainSlotMinsNum === 0;
  const chainCalculatedSlots = chainSlotMinsNum > 0 ? Math.floor(chainTotalMins / chainSlotMinsNum) : 0;
  const isChainValidDuration = chainHoursNum > 0 && chainHoursNum <= 24;

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep((prev) => prev + 1);
    } else {
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const toggleDayOfWeek = (day) => {
    const current = Array.isArray(daysOfWeek) ? daysOfWeek : [];
    if (current.includes(day)) {
      onDaysOfWeekChange(current.filter((d) => d !== day));
    } else {
      onDaysOfWeekChange([...current, day]);
    }
  };

  const setPresetDates = (daysOffset, totalDurationDays) => {
    const start = new Date();
    start.setDate(start.getDate() + daysOffset);
    const end = new Date(start);
    end.setDate(end.getDate() + (totalDurationDays - 1));
    onFromDateChange(formatDateStr(start));
    onToDateChange(formatDateStr(end));
    if (onNumberOfDaysChange) onNumberOfDaysChange(String(totalDurationDays));
  };

  const PRAYER_PLANS = [
    {
      id: "floating",
      label: "Floating",
      subtitle: "Flexible duration at self-pace",
      icon: <ScheduleIcon sx={{ fontSize: 18 }} />,
      helpTitle: "Floating Prayer",
      helpPurpose: "A flexible, self-paced prayer campaign with a target commitment duration (e.g. 7, 14, 21, or 40 days) where members pray at their own speed.",
      helpHowItWorks: "Does not require fixed calendar dates. When an intercessor subscribes, their personal day-by-day countdown begins immediately.",
      helpMobileEffect: "Displays an interactive day progress tracker (e.g. Day 3 of 21) on the mobile app home screen with sequential daily devotionals.",
    },
    {
      id: "fixed",
      label: "Fixed",
      subtitle: "Set calendar start & end dates",
      icon: <CalendarMonthIcon sx={{ fontSize: 18 }} />,
      helpTitle: "Fixed Calendar Prayer",
      helpPurpose: "A synchronized congregational prayer campaign anchored to specific real-world calendar start and end dates (e.g. 21-Day Daniel Fast, Holy Week Fast).",
      helpHowItWorks: "All registered members start together on the Start Date and conclude together on the End Date simultaneously.",
      helpMobileEffect: "Shows a countdown timer to the launch date, and locks daily prayer points to today's calendar date so everyone prays in unison.",
    },
    /*
    {
      id: "open",
      label: "Open",
      subtitle: "Continuous perpetual prayer room",
      icon: <AllInclusiveIcon sx={{ fontSize: 18 }} />,
      helpTitle: "Open Perpetual Prayer Room",
      helpPurpose: "An ongoing, perpetual prayer altar with no expiration or end date that remains open continuously.",
      helpHowItWorks: "Always active. Intercessors can drop in and pray anytime 24/7 without worrying about missing a campaign start date.",
      helpMobileEffect: "Permanently accessible on the mobile app home screen with a live 'Join Prayer Room' button.",
    },
    */
    {
      id: "chain",
      label: "Chain Prayer",
      subtitle: "24-hr continuous slot relay",
      icon: <LinkIcon sx={{ fontSize: 18 }} />,
      helpTitle: "Chain Prayer (24-Hour Continuous Slot Relay)",
      helpPurpose: "An unbroken, non-stop prayer relay where participants sign up for dedicated consecutive time slots to maintain unbroken prayer around the clock.",
      helpHowItWorks: "The total session duration (up to 24 hours) is divided into equal slots (15, 30, 45, or 60 mins). Members reserve their preferred slots so intercession never stops.",
      helpMobileEffect: "Displays a live interactive booking grid where users select their slot, receive push reminders before their turn, and check in live during prayer.",
    },
  ];

  const FREQUENCIES = [
    {
      id: "Daily",
      label: "Daily",
      subtitle: "Every consecutive day",
      icon: <TodayIcon sx={{ fontSize: 18 }} />,
      helpTitle: "Daily Prayer Frequency",
      helpPurpose: "Members pray every single day throughout the prayer journey.",
      helpHowItWorks: "Unlocks a fresh prayer point every morning and sends daily reminder push notifications.",
      helpMobileEffect: "Enables daily prayer streak badges and morning reminders on mobile.",
    },
    {
      id: "Weekly",
      label: "Weekly",
      subtitle: "Specific days of week",
      icon: <DateRangeIcon sx={{ fontSize: 18 }} />,
      helpTitle: "Weekly Prayer Frequency",
      helpPurpose: "Prayer occurs only on designated days of the week (e.g. every Wednesday and Friday).",
      helpHowItWorks: "Select your active weekdays below. The prayer room only activates on those chosen days.",
      helpMobileEffect: "Mobile notifications trigger only on the selected active weekdays.",
    },
    {
      id: "Custom",
      label: "Custom",
      subtitle: "Flexible targeted intervals",
      icon: <TuneIcon sx={{ fontSize: 18 }} />,
      helpTitle: "Custom Prayer Frequency",
      helpPurpose: "Flexible intervals for specialized church prayer schedules.",
      helpHowItWorks: "Allows administrators to establish custom prayer cadences.",
      helpMobileEffect: "Adapts to custom reminders configured for the campaign.",
    },
  ];

  const FLOATING_PRESETS = ["7", "14", "21", "30", "40"];

  const ALL_WEEKDAYS = [
    { key: "Sun", full: "Sunday" },
    { key: "Mon", full: "Monday" },
    { key: "Tue", full: "Tuesday" },
    { key: "Wed", full: "Wednesday" },
    { key: "Thu", full: "Thursday" },
    { key: "Fri", full: "Friday" },
    { key: "Sat", full: "Saturday" },
  ];

  const SLOT_OPTIONS = [
    { value: "15", label: "15 mins" },
    { value: "30", label: "30 mins" },
    { value: "45", label: "45 mins" },
    { value: "60", label: "60 mins" },
  ];

  return (
    <Box sx={{ width: "100%", py: 1 }}>
      {/* ── Top Progress Header ── */}
      {/* ── Top Header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.2}>
          <Box
            sx={{
              px: 1.5,
              py: 0.35,
              borderRadius: "20px",
              bgcolor: isLight ? "rgba(13, 148, 136, 0.1)" : "rgba(13, 148, 136, 0.2)",
              border: isLight ? "1px solid rgba(13, 148, 136, 0.25)" : "1px solid rgba(13, 148, 136, 0.4)",
              display: "flex",
              alignItems: "center",
              gap: 0.8,
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 15, color: isLight ? "#0D9488" : "#64D2E2" }} />
            <Typography sx={{ color: isLight ? "#0D9488" : "#64D2E2", fontWeight: 800, fontSize: "0.82rem" }}>
              Prayer Setup (1 Step)
            </Typography>
          </Box>
          <Typography sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.7)", fontSize: "0.84rem", fontWeight: 500 }}>
            Configure your prayer format, frequency, and time slots below in a single step.
          </Typography>
        </Box>

        <Button
          size="small"
          onClick={onSkip}
          sx={{
            color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.6)",
            textTransform: "none",
            fontSize: "0.82rem",
            fontWeight: 600,
            "&:hover": { color: isLight ? "#0F172A" : "#FFFFFF", bgcolor: "transparent" },
          }}
        >
          Skip questions, open full form →
        </Button>
      </Box>

      {/* ── ALL-IN-ONE 1-STEP CONFIGURATION ── */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 3.5,
        }}
      >
        {/* ============================================================ */}
        {/* SECTION 1: WHAT TYPE OF PRAYER DO YOU WANT TO START?         */}
        {/* ============================================================ */}
        <Box sx={{ width: "100%", boxSizing: "border-box" }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: isLight ? "#0F172A" : "#FFFFFF",
                fontSize: { xs: "1.15rem", md: "1.3rem" },
                letterSpacing: -0.2,
                mb: 0.5,
              }}
            >
              What type of prayer do you want to start?
            </Typography>
            <Typography sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.65)", mb: 2.5, fontSize: "0.88rem" }}>
              Choose a format to begin. We'll only ask questions relevant to this choice.
            </Typography>

            {/* Compact inline options */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              {PRAYER_PLANS.map((plan) => {
                const isSelected = prayerPlan === plan.id;
                return (
                  <Box
                    key={plan.id}
                    onClick={() => onPrayerPlanChange(plan.id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.2,
                      px: 2.2,
                      py: 1.2,
                      borderRadius: "10px",
                      cursor: "pointer",
                      border: isSelected
                        ? "2px solid #0D9488"
                        : isLight
                          ? "1.5px solid #CBD5E1"
                          : "1.5px solid rgba(255, 255, 255, 0.12)",
                      bgcolor: isSelected
                        ? (isLight ? "rgba(13, 148, 136, 0.08)" : "rgba(13, 148, 136, 0.22)")
                        : (isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.04)"),
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxShadow: isSelected ? "0 2px 8px rgba(13, 148, 136, 0.2)" : "none",
                      "&:hover": {
                        borderColor: "#0D9488",
                        bgcolor: isLight ? "rgba(13, 148, 136, 0.06)" : "rgba(255, 255, 255, 0.08)",
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <Box sx={{ color: isSelected ? "#0D9488" : (isLight ? "#94A3B8" : "rgba(255, 255, 255, 0.4)"), display: "flex" }}>
                      {isSelected ? (
                        <RadioButtonCheckedIcon sx={{ fontSize: 20, color: isLight ? "#0D9488" : "#64D2E2" }} />
                      ) : (
                        <RadioButtonUncheckedIcon sx={{ fontSize: 20 }} />
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
                        <Typography
                          sx={{
                            fontWeight: 750,
                            color: isSelected ? (isLight ? "#0F766E" : "#64D2E2") : (isLight ? "#0F172A" : "#FFFFFF"),
                            fontSize: "0.95rem",
                            lineHeight: 1.2,
                          }}
                        >
                          {plan.label}
                        </Typography>
                        <HelpTooltip
                          title={plan.helpTitle}
                          purpose={plan.helpPurpose}
                          howItWorks={plan.helpHowItWorks}
                          mobileEffect={plan.helpMobileEffect}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.6)",
                          fontSize: "0.78rem",
                          display: "block",
                          mt: 0.2,
                        }}
                      >
                        {plan.subtitle}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* ============================================================ */}
          {/* SECTION 2: HOW OFTEN WOULD YOU LIKE TO PRAY?                 */}
          {/* ============================================================ */}
          <Box
            sx={{
              width: "100%",
              boxSizing: "border-box",
              pt: 3,
              borderTop: isLight ? "1px dashed #CBD5E1" : "1px dashed rgba(255, 255, 255, 0.15)",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: isLight ? "#0F172A" : "#FFFFFF",
                fontSize: { xs: "1.15rem", md: "1.3rem" },
                letterSpacing: -0.2,
                mb: 0.5,
              }}
            >
              How often would you like to pray?
            </Typography>
            <Typography sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.65)", mb: 2.5, fontSize: "0.88rem" }}>
              {prayerPlan === "chain" ? "Cadence configuration for continuous slot coverage." : "Select the cadence for participating members."}
            </Typography>

            {prayerPlan === "chain" ? (
              <Box
                sx={{
                  p: 2.2,
                  borderRadius: "12px",
                  bgcolor: isLight ? "rgba(13, 148, 136, 0.08)" : "rgba(13, 148, 136, 0.18)",
                  border: "1.5px solid rgba(13, 148, 136, 0.35)",
                  maxWidth: 560,
                  mb: 2.5,
                }}
              >
                <Box display="flex" alignItems="center" gap={1.2} mb={1}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      bgcolor: isLight ? "#0D9488" : "#14B8A6",
                      color: "#FFFFFF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <LinkIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ color: isLight ? "#0F766E" : "#64D2E2", fontWeight: 800, fontSize: "0.98rem" }}>
                      Continuous Non-Stop Chain Relay
                    </Typography>
                    <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.65)" }}>
                      Unbroken intercession covering consecutive prayer slots
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ color: isLight ? "#334155" : "rgba(255, 255, 255, 0.8)", fontSize: "0.84rem", lineHeight: 1.45 }}>
                  In a Chain Prayer, the cadence is continuous. Slots run seamlessly one after another across the configured session duration (up to 24 hours), dividing time amongst registered intercessors.
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.5,
                  mb: 2.5,
                }}
              >
                {FREQUENCIES.map((freq) => {
                  const isSelected = prayerFrequency === freq.id;
                  return (
                    <Box
                      key={freq.id}
                      onClick={() => onPrayerFrequencyChange(freq.id)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.2,
                        px: 2.2,
                        py: 1.2,
                        borderRadius: "10px",
                        cursor: "pointer",
                        border: isSelected
                          ? "2px solid #0D9488"
                          : isLight
                            ? "1.5px solid #CBD5E1"
                            : "1.5px solid rgba(255, 255, 255, 0.12)",
                        bgcolor: isSelected
                          ? (isLight ? "rgba(13, 148, 136, 0.08)" : "rgba(13, 148, 136, 0.22)")
                          : (isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.04)"),
                        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        boxShadow: isSelected ? "0 2px 8px rgba(13, 148, 136, 0.2)" : "none",
                        "&:hover": {
                          borderColor: "#0D9488",
                          bgcolor: isLight ? "rgba(13, 148, 136, 0.06)" : "rgba(255, 255, 255, 0.08)",
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      <Box sx={{ color: isSelected ? "#0D9488" : (isLight ? "#94A3B8" : "rgba(255, 255, 255, 0.4)"), display: "flex" }}>
                        {isSelected ? (
                          <RadioButtonCheckedIcon sx={{ fontSize: 20, color: isLight ? "#0D9488" : "#64D2E2" }} />
                        ) : (
                          <RadioButtonUncheckedIcon sx={{ fontSize: 20 }} />
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
                          <Typography
                            sx={{
                              fontWeight: 750,
                              color: isSelected ? (isLight ? "#0F766E" : "#64D2E2") : (isLight ? "#0F172A" : "#FFFFFF"),
                              fontSize: "0.95rem",
                              lineHeight: 1.2,
                            }}
                          >
                            {freq.label}
                          </Typography>
                          <HelpTooltip
                            title={freq.helpTitle}
                            purpose={freq.helpPurpose}
                            howItWorks={freq.helpHowItWorks}
                            mobileEffect={freq.helpMobileEffect}
                          />
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.6)",
                            fontSize: "0.78rem",
                            display: "block",
                            mt: 0.2,
                          }}
                        >
                          {freq.subtitle}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* If Weekly: Weekday buttons */}
            {prayerPlan !== "chain" && prayerFrequency === "Weekly" && (
              <Box
                sx={{
                  p: 1.8,
                  borderRadius: "10px",
                  bgcolor: isLight ? "#F8FAFC" : "rgba(0, 0, 0, 0.25)",
                  border: isLight ? "1px solid #CBD5E1" : "1px solid rgba(100, 210, 226, 0.3)",
                  maxWidth: 520,
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography sx={{ color: isLight ? "#0F766E" : "#64D2E2", fontWeight: 700, fontSize: "0.82rem" }}>
                    Select active prayer days:
                  </Typography>
                  <HelpTooltip
                    title="Active Prayer Days"
                    purpose="Determine which days of the week members participate in prayer."
                    howItWorks="On unselected days, members have rest days and do not receive alerts."
                    mobileEffect="The weekly schedule checklist in the mobile app displays only these selected days."
                  />
                </Box>
                <Box display="flex" justifyContent="space-between" gap={0.8}>
                  {ALL_WEEKDAYS.map((day) => {
                    const isDaySelected = Array.isArray(daysOfWeek) && daysOfWeek.includes(day.full);
                    return (
                      <Box
                        key={day.key}
                        onClick={() => toggleDayOfWeek(day.full)}
                        sx={{
                          flex: 1,
                          py: 0.8,
                          textAlign: "center",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontSize: "0.82rem",
                          fontWeight: 700,
                          transition: "all 0.18s",
                          bgcolor: isDaySelected
                            ? "#0D9488"
                            : (isLight ? "#FFFFFF" : "rgba(255, 255, 255, 0.05)"),
                          color: isDaySelected
                            ? "#FFFFFF"
                            : (isLight ? "#475569" : "rgba(255, 255, 255, 0.7)"),
                          border: isDaySelected
                            ? "1.5px solid #0D9488"
                            : (isLight ? "1px solid #CBD5E1" : "1px solid rgba(255, 255, 255, 0.15)"),
                          "&:hover": {
                            bgcolor: isDaySelected ? "#0F766E" : (isLight ? "#E2E8F0" : "rgba(255, 255, 255, 0.1)"),
                          },
                        }}
                      >
                        {day.key}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>

          {/* ============================================================ */}
          {/* SECTION 3: WHEN WOULD YOU LIKE TO START / DURATION?          */}
          {/* ============================================================ */}
          <Box
            sx={{
              width: "100%",
              boxSizing: "border-box",
              pt: 3,
              borderTop: isLight ? "1px dashed #CBD5E1" : "1px dashed rgba(255, 255, 255, 0.15)",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: isLight ? "#0F172A" : "#FFFFFF",
                fontSize: { xs: "1.15rem", md: "1.3rem" },
                letterSpacing: -0.2,
                mb: 0.5,
              }}
            >
              {prayerPlan === "chain"
                ? "Chain Prayer Duration & Slot Setup"
                : prayerPlan === "floating"
                  ? "How long will this prayer run?"
                  : "When would you like to start?"}
            </Typography>
            <Typography sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.65)", mb: 2.5, fontSize: "0.88rem" }}>
              {prayerPlan === "chain"
                ? "Configure total relay duration (max 24 hours), slot duration, and start time."
                : prayerPlan === "floating"
                  ? "Choose commitment days and slot duration per participant."
                  : "Choose calendar start/end dates and individual slot duration."}
            </Typography>

            {prayerPlan === "chain" ? (
              /* Chain Prayer Setup Section */
              <Box mb={2.5}>
                {/* Multi-Day Presets for Chain Prayer */}
                <Box mb={2}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.8}>
                    <Typography sx={{ color: isLight ? "#334155" : "rgba(255, 255, 255, 0.85)", fontWeight: 700, fontSize: "0.85rem" }}>
                      Chain Prayer Days:
                    </Typography>
                    <Typography sx={{ fontSize: "0.78rem", color: "#0D9488", fontWeight: 700 }}>
                      {(() => {
                        if (!fromDate) return "1 Day (Default)";
                        const start = new Date(fromDate);
                        const end = toDate ? new Date(toDate) : start;
                        const diff = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1);
                        return `${diff} ${diff === 1 ? 'Day (Single Day Vigil)' : 'Days (Multi-Day Chain)'}`;
                      })()}
                    </Typography>
                  </Box>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {[
                      { days: 1, label: "1 Day (Default)" },
                      { days: 3, label: "3 Days" },
                      { days: 7, label: "7 Days" },
                      { days: 14, label: "14 Days" },
                      { days: 21, label: "21 Days" },
                      { days: 40, label: "40 Days" },
                    ].map(({ days, label }) => {
                      const currentDiff = fromDate && toDate
                        ? Math.max(1, Math.ceil((new Date(toDate) - new Date(fromDate)) / (1000 * 60 * 60 * 24)) + 1)
                        : 1;
                      const isSelected = !isChainCustomDays && currentDiff === days;
                      return (
                        <Box
                          key={days}
                          onClick={() => {
                            setIsChainCustomDays(false);
                            setChainCustomDaysVal("");
                            const newStart = fromDate ? new Date(fromDate) : new Date();
                            if (!fromDate && onFromDateChange) onFromDateChange(formatDateStr(newStart));
                            const newEnd = new Date(newStart);
                            newEnd.setDate(newEnd.getDate() + (days - 1));
                            if (onToDateChange) onToDateChange(formatDateStr(newEnd));
                            if (onNumberOfDaysChange) onNumberOfDaysChange(String(days));
                          }}
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            bgcolor: isSelected
                              ? (isLight ? "rgba(13, 148, 136, 0.12)" : "rgba(13, 148, 136, 0.25)")
                              : (isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)"),
                            border: isSelected
                              ? "2px solid #0D9488"
                              : (isLight ? "1px solid #CBD5E1" : "1px solid rgba(255, 255, 255, 0.15)"),
                            color: isSelected ? (isLight ? "#0F766E" : "#64D2E2") : (isLight ? "#0F172A" : "#FFFFFF"),
                            "&:hover": { borderColor: "#0D9488" },
                          }}
                        >
                          {label}
                        </Box>
                      );
                    })}
                    {/* Custom Days Button for Chain Prayer */}
                    {(() => {
                      const currentDiff = fromDate && toDate
                        ? Math.max(1, Math.ceil((new Date(toDate) - new Date(fromDate)) / (1000 * 60 * 60 * 24)) + 1)
                        : 1;
                      const isKnownPreset = [1, 3, 7, 14, 21, 40].includes(currentDiff);
                      const isCustomActive = isChainCustomDays || (!isKnownPreset && currentDiff > 1);
                      return (
                        <Box
                          onClick={() => {
                            setIsChainCustomDays(true);
                            if (!chainCustomDaysVal) setChainCustomDaysVal(String(currentDiff));
                          }}
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            bgcolor: isCustomActive
                              ? (isLight ? "rgba(13, 148, 136, 0.12)" : "rgba(13, 148, 136, 0.25)")
                              : (isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)"),
                            border: isCustomActive
                              ? "2px solid #0D9488"
                              : (isLight ? "1px solid #CBD5E1" : "1px solid rgba(255, 255, 255, 0.15)"),
                            color: isCustomActive ? (isLight ? "#0F766E" : "#64D2E2") : (isLight ? "#0F172A" : "#FFFFFF"),
                            "&:hover": { borderColor: "#0D9488" },
                          }}
                        >
                          + Custom Days
                        </Box>
                      );
                    })()}
                  </Box>
                  {/* Custom Days Input for Chain Prayer */}
                  {(() => {
                    const currentDiff = fromDate && toDate
                      ? Math.max(1, Math.ceil((new Date(toDate) - new Date(fromDate)) / (1000 * 60 * 60 * 24)) + 1)
                      : 1;
                    const isKnownPreset = [1, 3, 7, 14, 21, 40].includes(currentDiff);
                    const isCustomActive = isChainCustomDays || (!isKnownPreset && currentDiff > 1);
                    if (!isCustomActive) return null;
                    return (
                      <Box mt={1.2} maxWidth={320}>
                        <TextField
                          size="small"
                          label="Custom Chain Prayer Duration"
                          placeholder="e.g. 10, 50, 100"
                          type="number"
                          value={chainCustomDaysVal || currentDiff}
                          onChange={(e) => {
                            const val = e.target.value;
                            setChainCustomDaysVal(val);
                            const num = parseInt(val, 10);
                            if (!isNaN(num) && num > 0) {
                              const newStart = fromDate ? new Date(fromDate) : new Date();
                              if (!fromDate && onFromDateChange) onFromDateChange(formatDateStr(newStart));
                              const newEnd = new Date(newStart);
                              newEnd.setDate(newEnd.getDate() + (num - 1));
                              if (onToDateChange) onToDateChange(formatDateStr(newEnd));
                              if (onNumberOfDaysChange) onNumberOfDaysChange(String(num));
                            }
                          }}
                          InputProps={{
                            endAdornment: <Typography sx={{ fontSize: "0.8rem", color: isLight ? "#64748B" : "rgba(255,255,255,0.6)", ml: 0.5 }}>Days</Typography>
                          }}
                          sx={{
                            width: "100%",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)",
                            },
                            "& input": { py: 0.8, fontSize: "0.85rem" },
                          }}
                        />
                      </Box>
                    );
                  })()}
                </Box>

                <Box display="flex" gap={2} flexWrap="wrap" maxWidth={680} mb={2}>
                  <Box flex="1 1 200px">
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                      <Typography sx={{ color: isLight ? "#475569" : "rgba(255, 255, 255, 0.8)", fontSize: "0.82rem", fontWeight: 600 }}>
                        Start Date *
                      </Typography>
                      <HelpTooltip
                        title="Chain Relay Start Date"
                        purpose="The calendar date when the unbroken chain prayer begins."
                        howItWorks="The very first prayer slot commences on this day at the configured Start Time."
                        mobileEffect="A countdown clock on the mobile app counts down to this start date and time."
                      />
                    </Box>
                    <TextField
                      size="small"
                      fullWidth
                      type="date"
                      value={fromDate}
                      onChange={(e) => {
                        const newFrom = e.target.value;
                        onFromDateChange(newFrom);
                        if (!toDate || toDate < newFrom) {
                          if (onToDateChange) onToDateChange(newFrom);
                        }
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)",
                        },
                      }}
                    />
                  </Box>
                  <Box flex="1 1 200px">
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                      <Typography sx={{ color: isLight ? "#475569" : "rgba(255, 255, 255, 0.8)", fontSize: "0.82rem", fontWeight: 600 }}>
                        End Date (Multi-Day Chain)
                      </Typography>
                      <HelpTooltip
                        title="Chain Relay End Date"
                        purpose="Enables running the continuous chain relay across multiple days."
                        howItWorks="If set to a later date, the chain prayer spans from Start Date to End Date. If same as Start Date, it runs for 1 day."
                        mobileEffect="Members can book slots across all days of the multi-day chain campaign."
                      />
                    </Box>
                    <TextField
                      size="small"
                      fullWidth
                      type="date"
                      value={toDate || fromDate}
                      onChange={(e) => onToDateChange(e.target.value)}
                      inputProps={{ min: fromDate }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)",
                        },
                      }}
                    />
                  </Box>
                  <Box flex="1 1 200px">
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                      <Typography sx={{ color: isLight ? "#475569" : "rgba(255, 255, 255, 0.8)", fontSize: "0.82rem", fontWeight: 600 }}>
                        Start Time *
                      </Typography>
                      <HelpTooltip
                        title="Chain Relay Kickoff Time"
                        purpose="The exact hour and minute the first slot begins."
                        howItWorks="Subsequent prayer slots are chained sequentially one after another starting from this time."
                        mobileEffect="Mobile users booked in the opening slot receive an immediate 15-minute advance reminder."
                      />
                    </Box>
                    <TextField
                      size="small"
                      fullWidth
                      type="time"
                      value={fromTime || "00:00"}
                      onChange={(e) => onFromTimeChange(e.target.value)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)",
                        },
                      }}
                    />
                  </Box>
                </Box>

                {/* Prayer Duration (Hours, max 24) */}
                <Box mb={2}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                    <Box display="flex" alignItems="center">
                      <Typography sx={{ color: isLight ? "#334155" : "rgba(255, 255, 255, 0.8)", fontWeight: 700, fontSize: "0.85rem" }}>
                        Prayer Duration (Max 24 Hours):
                      </Typography>
                      <HelpTooltip
                        title="Relay Total Duration (Max 24h)"
                        purpose="The entire continuous intercession period to be covered without interruption."
                        howItWorks="Enter up to 24 hours (e.g. 6h, 12h, 18h, or a full 24h unbroken vigil). The system slices this span into consecutive slots."
                        mobileEffect="Renders an unbroken 24-hr circular timeline on mobile showing live slot occupancy and vacant slots."
                      />
                    </Box>
                    <Typography sx={{ fontSize: "0.78rem", color: chainHoursNum > 24 ? "#EF4444" : "#0D9488", fontWeight: 700 }}>
                      {chainHoursNum} Hours {chainHoursNum === 24 ? "(One Full Day)" : ""}
                    </Typography>
                  </Box>

                  {/* Quick Duration Presets */}
                  <Box display="flex" flexWrap="wrap" gap={1} mb={1.2}>
                    {["6", "12", "18", "24"].map((hrs) => {
                      const isSelected = String(chainDurationHours) === hrs;
                      return (
                        <Box
                          key={hrs}
                          onClick={() => onChainDurationHoursChange(hrs)}
                          sx={{
                            px: 1.8,
                            py: 0.6,
                            textAlign: "center",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.82rem",
                            fontWeight: 700,
                            bgcolor: isSelected
                              ? (isLight ? "rgba(13, 148, 136, 0.12)" : "rgba(13, 148, 136, 0.25)")
                              : (isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)"),
                            border: isSelected
                              ? "2px solid #0D9488"
                              : (isLight ? "1px solid #CBD5E1" : "1px solid rgba(255, 255, 255, 0.15)"),
                            color: isSelected ? (isLight ? "#0F766E" : "#64D2E2") : (isLight ? "#0F172A" : "#FFFFFF"),
                            "&:hover": { borderColor: "#0D9488" },
                          }}
                        >
                          {hrs} Hours {hrs === "24" ? "★ Default" : ""}
                        </Box>
                      );
                    })}
                  </Box>

                  <TextField
                    size="small"
                    placeholder="Enter duration in hours (1 - 24)"
                    type="number"
                    value={chainDurationHours}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || (Number(val) >= 0 && Number(val) <= 24)) {
                        onChainDurationHoursChange(val);
                      }
                    }}
                    inputProps={{ min: 1, max: 24, step: 1 }}
                    helperText={chainHoursNum > 24 ? "Cannot exceed 24 hours" : "Default is 24 hours. You can reduce to any value below 24 hours."}
                    error={chainHoursNum > 24 || chainHoursNum <= 0}
                    sx={{
                      maxWidth: 340,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)",
                      },
                      "& input": { py: 0.8, fontSize: "0.85rem" },
                    }}
                  />
                </Box>

                {/* Prayer Slot Duration (15, 30, 45, 60 mins) */}
                <Box mb={2}>
                  <Box display="flex" alignItems="center" mb={0.8}>
                    <Typography sx={{ color: isLight ? "#334155" : "rgba(255, 255, 255, 0.8)", fontWeight: 700, fontSize: "0.85rem" }}>
                      Prayer Slot Duration:
                    </Typography>
                    <HelpTooltip
                      title="Slot Duration Length"
                      purpose="The length of each individual prayer commitment (15, 30, 45, or 60 minutes)."
                      howItWorks="Total relay duration is divided by this duration to generate consecutive non-overlapping slots."
                      mobileEffect="Intercessors on the mobile app can claim any available 15, 30, 45, or 60 min slot."
                    />
                  </Box>
                  <Box display="flex" gap={1.2}>
                    {SLOT_OPTIONS.map((s) => {
                      const isSelected = String(slotDuration) === s.value;
                      return (
                        <Box
                          key={s.value}
                          onClick={() => onSlotDurationChange(s.value)}
                          sx={{
                            px: 1.8,
                            py: 0.7,
                            textAlign: "center",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            bgcolor: isSelected
                              ? (isLight ? "rgba(13, 148, 136, 0.12)" : "rgba(13, 148, 136, 0.25)")
                              : (isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)"),
                            border: isSelected
                              ? "2px solid #0D9488"
                              : (isLight ? "1px solid #CBD5E1" : "1px solid rgba(255, 255, 255, 0.15)"),
                            color: isSelected ? (isLight ? "#0F766E" : "#64D2E2") : (isLight ? "#0F172A" : "#FFFFFF"),
                            "&:hover": { borderColor: "#0D9488" },
                          }}
                        >
                          {s.label}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {/* Live Automatic Slot Generation & Divisibility Result */}
                <Box
                  sx={{
                    p: 1.8,
                    borderRadius: "10px",
                    bgcolor: isChainDivisible
                      ? (isLight ? "rgba(13, 148, 136, 0.08)" : "rgba(13, 148, 136, 0.2)")
                      : (isLight ? "rgba(239, 68, 68, 0.08)" : "rgba(239, 68, 68, 0.2)"),
                    border: `1.5px solid ${isChainDivisible ? "#0D9488" : "#EF4444"}`,
                    maxWidth: 560,
                  }}
                >
                  {isChainDivisible ? (
                    <Box>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.4}>
                        <Box display="flex" alignItems="center" gap={0.8}>
                          <AutoAwesomeIcon sx={{ fontSize: 18, color: isLight ? "#0D9488" : "#64D2E2" }} />
                          <Typography sx={{ color: isLight ? "#0F766E" : "#64D2E2", fontWeight: 800, fontSize: "0.9rem" }}>
                            Automatic Slot Generation: {chainCalculatedSlots} Prayer Slots
                          </Typography>
                        </Box>
                        <HelpTooltip
                          title="Automatic Slot Generation"
                          purpose="Calculates total slots available based on total relay time and slot length."
                          howItWorks="For example, 24 hours (1440 mins) ÷ 30 mins = 48 continuous slots for 48 intercessors."
                          mobileEffect="Generates 48 distinct slots in the mobile booking screen with individual intercessor assignments."
                        />
                      </Box>
                      <Typography sx={{ color: isLight ? "#334155" : "rgba(255, 255, 255, 0.8)", fontSize: "0.82rem", lineHeight: 1.4 }}>
                        Dividing {chainHoursNum} hours ({chainTotalMins} mins) by {slotDuration}-minute slots automatically generates <strong>{chainCalculatedSlots} prayer slots</strong>.
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Box display="flex" alignItems="center" gap={0.8} mb={0.4}>
                        <WarningAmberIcon sx={{ fontSize: 18, color: "#EF4444" }} />
                        <Typography sx={{ color: "#EF4444", fontWeight: 800, fontSize: "0.9rem" }}>
                          Total Duration Must Be Divisible By Slot Duration
                        </Typography>
                      </Box>
                      <Typography sx={{ color: isLight ? "#991B1B" : "#FCA5A5", fontSize: "0.82rem", lineHeight: 1.4 }}>
                        {chainTotalMins} minutes cannot be divided evenly into {slotDuration}-minute slots (remaining time: {chainTotalMins % chainSlotMinsNum} mins). Please adjust duration or slot length.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            ) : prayerPlan === "floating" ? (
              <Box mb={2.5}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Typography sx={{ color: isLight ? "#334155" : "rgba(255, 255, 255, 0.8)", fontWeight: 700, fontSize: "0.85rem" }}>
                    Duration in Days:
                  </Typography>
                  <HelpTooltip
                    title="Target Duration in Days"
                    purpose="Total number of consecutive days of prayer required to complete this campaign."
                    howItWorks="Members advance one day at a time at their own pace (e.g. 21 days or 40 days)."
                    mobileEffect="Displays an interactive completion progress ring (e.g. Day 7 of 21) on the user's mobile dashboard."
                  />
                </Box>
                <Box display="flex" flexWrap="wrap" gap={1} mb={1.5}>
                  {FLOATING_PRESETS.map((days) => {
                    const isSelected = !isFloatingCustomDays && String(numberOfDays) === days;
                    return (
                      <Box
                        key={days}
                        onClick={() => {
                          setIsFloatingCustomDays(false);
                          onNumberOfDaysChange(days);
                        }}
                        sx={{
                          px: 2,
                          py: 0.8,
                          textAlign: "center",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontSize: "0.85rem",
                          fontWeight: 700,
                          bgcolor: isSelected
                            ? (isLight ? "rgba(13, 148, 136, 0.12)" : "rgba(13, 148, 136, 0.25)")
                            : (isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)"),
                          border: isSelected
                            ? "2px solid #0D9488"
                            : (isLight ? "1px solid #CBD5E1" : "1px solid rgba(255, 255, 255, 0.15)"),
                          color: isSelected ? (isLight ? "#0F766E" : "#64D2E2") : (isLight ? "#0F172A" : "#FFFFFF"),
                          "&:hover": {
                            borderColor: "#0D9488",
                          },
                        }}
                      >
                        {days} Days
                      </Box>
                    );
                  })}
                  {/* Custom Days Button for Floating */}
                  {(() => {
                    const isCustomActive = isFloatingCustomDays || !FLOATING_PRESETS.includes(String(numberOfDays));
                    return (
                      <Box
                        onClick={() => {
                          setIsFloatingCustomDays(true);
                          if (FLOATING_PRESETS.includes(String(numberOfDays))) {
                            onNumberOfDaysChange("50");
                          }
                        }}
                        sx={{
                          px: 2,
                          py: 0.8,
                          textAlign: "center",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontSize: "0.85rem",
                          fontWeight: 700,
                          bgcolor: isCustomActive
                            ? (isLight ? "rgba(13, 148, 136, 0.12)" : "rgba(13, 148, 136, 0.25)")
                            : (isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)"),
                          border: isCustomActive
                            ? "2px solid #0D9488"
                            : (isLight ? "1px solid #CBD5E1" : "1px solid rgba(255, 255, 255, 0.15)"),
                          color: isCustomActive ? (isLight ? "#0F766E" : "#64D2E2") : (isLight ? "#0F172A" : "#FFFFFF"),
                          "&:hover": { borderColor: "#0D9488" },
                        }}
                      >
                        + Custom Days
                      </Box>
                    );
                  })()}
                </Box>
                {/* Custom Days Input for Floating */}
                {(isFloatingCustomDays || !FLOATING_PRESETS.includes(String(numberOfDays))) && (
                  <Box maxWidth={320} mb={1}>
                    <TextField
                      size="small"
                      label="Custom Duration in Days"
                      placeholder="e.g. 50, 100"
                      type="number"
                      value={numberOfDays}
                      onChange={(e) => onNumberOfDaysChange(e.target.value)}
                      InputProps={{
                        endAdornment: <Typography sx={{ fontSize: "0.8rem", color: isLight ? "#64748B" : "rgba(255,255,255,0.6)", ml: 0.5 }}>Days</Typography>
                      }}
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)",
                        },
                        "& input": { py: 0.8, fontSize: "0.85rem" },
                      }}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              /* Fixed / Open: Date pickers */
              <Box mb={2.5}>
                {/* Duration Quick-Preset Chips */}
                <Typography sx={{ color: isLight ? "#475569" : "rgba(255,255,255,0.7)", fontSize: "0.78rem", fontWeight: 600, mb: 0.8 }}>
                  Quick Duration (starts today)
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.8} mb={1.5}>
                  {[21, 30, 60, 90, 120].map((days) => {
                    const todayStr = new Date().toISOString().split("T")[0];
                    const isSelected = !isFixedCustomDays && fromDate === todayStr &&
                      (() => { const e = new Date(); e.setDate(e.getDate() + days - 1); return toDate === e.toISOString().split("T")[0]; })();
                    return (
                      <Box
                        key={days}
                        onClick={() => {
                          setIsFixedCustomDays(false);
                          setFixedCustomDaysVal("");
                          setPresetDates(0, days);
                        }}
                        sx={{
                          px: 1.8,
                          py: 0.6,
                          borderRadius: "20px",
                          cursor: "pointer",
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          border: isSelected
                            ? "2px solid #0D9488"
                            : isLight ? "1.5px solid #CBD5E1" : "1.5px solid rgba(255,255,255,0.2)",
                          bgcolor: isSelected
                            ? isLight ? "rgba(13,148,136,0.12)" : "rgba(13,148,136,0.25)"
                            : isLight ? "#F8FAFC" : "rgba(255,255,255,0.05)",
                          color: isSelected
                            ? isLight ? "#0F766E" : "#64D2E2"
                            : isLight ? "#0F172A" : "#FFFFFF",
                          transition: "all 0.18s ease",
                          "&:hover": {
                            borderColor: "#0D9488",
                            color: isLight ? "#0F766E" : "#64D2E2",
                          },
                        }}
                      >
                        {days} Days
                      </Box>
                    );
                  })}
                  {/* Custom Days Button for Fixed */}
                  {(() => {
                    const curDiff = fromDate && toDate
                      ? Math.max(1, Math.ceil((new Date(toDate) - new Date(fromDate)) / (1000 * 60 * 60 * 24)) + 1)
                      : 21;
                    const isKnownPreset = [21, 30, 60, 90, 120].includes(curDiff);
                    const isCustomActive = isFixedCustomDays || (!isKnownPreset && curDiff !== 21);
                    return (
                      <Box
                        onClick={() => {
                          setIsFixedCustomDays(true);
                          if (!fixedCustomDaysVal) {
                            setFixedCustomDaysVal(String(curDiff));
                          }
                        }}
                        sx={{
                          px: 1.8,
                          py: 0.6,
                          borderRadius: "20px",
                          cursor: "pointer",
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          border: isCustomActive
                            ? "2px solid #0D9488"
                            : isLight ? "1.5px solid #CBD5E1" : "1.5px solid rgba(255,255,255,0.2)",
                          bgcolor: isCustomActive
                            ? isLight ? "rgba(13,148,136,0.12)" : "rgba(13,148,136,0.25)"
                            : isLight ? "#F8FAFC" : "rgba(255,255,255,0.05)",
                          color: isCustomActive
                            ? isLight ? "#0F766E" : "#64D2E2"
                            : isLight ? "#0F172A" : "#FFFFFF",
                          transition: "all 0.18s ease",
                          "&:hover": {
                            borderColor: "#0D9488",
                            color: isLight ? "#0F766E" : "#64D2E2",
                          },
                        }}
                      >
                        + Custom Days
                      </Box>
                    );
                  })()}
                </Box>
                {/* Custom Days Input for Fixed */}
                {(() => {
                  const curDiff = fromDate && toDate
                    ? Math.max(1, Math.ceil((new Date(toDate) - new Date(fromDate)) / (1000 * 60 * 60 * 24)) + 1)
                    : 21;
                  const isKnownPreset = [21, 30, 60, 90, 120].includes(curDiff);
                  const isCustomActive = isFixedCustomDays || (!isKnownPreset && curDiff !== 21);
                  if (!isCustomActive) return null;
                  return (
                    <Box mb={2} maxWidth={320}>
                      <TextField
                        size="small"
                        label="Custom Fixed Duration"
                        placeholder="e.g. 50, 100"
                        type="number"
                        value={fixedCustomDaysVal || curDiff}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFixedCustomDaysVal(val);
                          const num = parseInt(val, 10);
                          if (!isNaN(num) && num > 0) {
                            const start = fromDate ? new Date(fromDate) : new Date();
                            if (!fromDate && onFromDateChange) onFromDateChange(formatDateStr(start));
                            const end = new Date(start);
                            end.setDate(end.getDate() + (num - 1));
                            onToDateChange(formatDateStr(end));
                            if (onNumberOfDaysChange) onNumberOfDaysChange(String(num));
                          }
                        }}
                        InputProps={{
                          endAdornment: <Typography sx={{ fontSize: "0.8rem", color: isLight ? "#64748B" : "rgba(255,255,255,0.6)", ml: 0.5 }}>Days</Typography>
                        }}
                        sx={{
                          width: "100%",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)",
                          },
                          "& input": { py: 0.8, fontSize: "0.85rem" },
                        }}
                      />
                    </Box>
                  );
                })()}

                <Box display="flex" gap={2} flexWrap="wrap" maxWidth={460}>
                  <Box flex="1 1 200px">
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                      <Typography sx={{ color: isLight ? "#475569" : "rgba(255, 255, 255, 0.8)", fontSize: "0.82rem", fontWeight: 600 }}>
                        Start Date
                      </Typography>
                      <HelpTooltip
                        title="Campaign Start Date"
                        purpose="The synchronized calendar date when the corporate prayer begins."
                        howItWorks="All church members begin praying on this date simultaneously."
                        mobileEffect="A countdown banner on mobile reminds members to get ready for the launch date."
                      />
                    </Box>
                    <TextField
                      size="small"
                      fullWidth
                      type="date"
                      value={fromDate}
                      onChange={(e) => onFromDateChange(e.target.value)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)",
                        },
                      }}
                    />
                  </Box>
                  <Box flex="1 1 200px">
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                      <Typography sx={{ color: isLight ? "#475569" : "rgba(255, 255, 255, 0.8)", fontSize: "0.82rem", fontWeight: 600 }}>
                        End Date
                      </Typography>
                      <HelpTooltip
                        title="Campaign End Date"
                        purpose="The final calendar date of this prayer campaign."
                        howItWorks="The campaign concludes on this date for all participants simultaneously."
                        mobileEffect="A final celebratory completion badge is awarded to members who complete all days."
                      />
                    </Box>
                    <TextField
                      size="small"
                      fullWidth
                      type="date"
                      value={toDate}
                      onChange={(e) => onToDateChange(e.target.value)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)",
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {/* Slot Duration Choice for non-chain */}
            {prayerPlan !== "chain" && (
              <Box>
                <Box display="flex" alignItems="center" mb={1}>
                  <Typography sx={{ color: isLight ? "#334155" : "rgba(255, 255, 255, 0.8)", fontWeight: 700, fontSize: "0.85rem" }}>
                    Prayer Slot Duration:
                  </Typography>
                  <HelpTooltip
                    title="Session Slot Duration"
                    purpose="The duration allocated for each daily prayer session."
                    howItWorks="Helps schedule reminders and manage in-room prayer timer duration."
                    mobileEffect="Guides the user through their daily prayer session using an interactive mobile prayer room timer."
                  />
                </Box>
                <Box display="flex" gap={1.2}>
                  {SLOT_OPTIONS.map((s) => {
                    const isSelected = String(slotDuration) === s.value;
                    return (
                      <Box
                        key={s.value}
                        onClick={() => onSlotDurationChange(s.value)}
                        sx={{
                          px: 2,
                          py: 0.8,
                          textAlign: "center",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontSize: "0.85rem",
                          fontWeight: 700,
                          bgcolor: isSelected
                            ? (isLight ? "rgba(13, 148, 136, 0.12)" : "rgba(13, 148, 136, 0.25)")
                            : (isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)"),
                          border: isSelected
                            ? "2px solid #0D9488"
                            : (isLight ? "1px solid #CBD5E1" : "1px solid rgba(255, 255, 255, 0.15)"),
                          color: isSelected ? (isLight ? "#0F766E" : "#64D2E2") : (isLight ? "#0F172A" : "#FFFFFF"),
                          "&:hover": {
                            borderColor: "#0D9488",
                          },
                        }}
                      >
                        {s.label}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* ── Action Button (1 Step) ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 3,
            pt: 2.5,
            borderTop: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255, 255, 255, 0.08)",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
              Ready to create prayer themes & devotionals?
            </Typography>
            <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.65)" }}>
              All schedule settings above are configured. Click below to continue to the main prayer form.
            </Typography>
          </Box>

          {(() => {
            const isFinalStepDisabled = isChain
              ? (!isChainValidDuration || !isChainDivisible || !fromDate)
              : (!fromDate || (prayerPlan === "fixed" && !toDate));

            return (
              <Button
                variant="contained"
                disabled={isFinalStepDisabled}
                endIcon={<AutoAwesomeIcon sx={{ fontSize: 18 }} />}
                onClick={onComplete}
                sx={{
                  background: isFinalStepDisabled
                    ? "#94A3B8"
                    : "linear-gradient(135deg, #0D9488 0%, #14B8A6 100%)",
                  color: "#FFFFFF",
                  textTransform: "none",
                  fontWeight: 750,
                  fontSize: "0.92rem",
                  px: 3.5,
                  py: 1.1,
                  borderRadius: "10px",
                  boxShadow: "0 2px 10px rgba(13, 148, 136, 0.35)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #0F766E 0%, #0D9488 100%)",
                  },
                  "&.Mui-disabled": {
                    bgcolor: isLight ? "#CBD5E1" : "rgba(255, 255, 255, 0.12)",
                    color: isLight ? "#94A3B8" : "rgba(255, 255, 255, 0.3)",
                  },
                }}
              >
                Continue to Prayer Content & Themes ✨
              </Button>
            );
          })()}
        </Box>
    </Box>
  );
};

export default PrayerWizardSetup;
